// File: frontend/src/components/KGIndicatorMatches.tsx
// Knowledge Graph companion panel for IOC Search: alongside the flat
// database lookup, shows whether the searched value exists as a canonical
// entity in the Knowledge Graph, with a direct link to its profile
// (connected malware/actors/CVEs, cross-report timeline).
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import useSWR from 'swr';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Stack,
    Button,
    Skeleton,
    alpha,
} from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { useApi } from '../hooks/useApi';
import { cyber } from '../theme';

interface KGMatch {
    id: string;
    name: string;
    type: string;
    report_count: number;
}

interface KGSearchResponse {
    total: number;
    results: KGMatch[];
}

const KG_COLOR = '#a55eea';

/**
 * Shown after every IOC search, whether or not the graph has a match:
 * the user must always see that TWO different lookups ran — the flat IOC
 * database and the cross-report Knowledge Graph.
 */
const KGIndicatorMatches: React.FC<{ query: string }> = ({ query }) => {
    const { authenticatedFetcher } = useApi();

    const { data, error, isLoading } = useSWR<KGSearchResponse>(
        query ? `/api/kg/search?query=${encodeURIComponent(query)}&entity_type=Indicator&limit=5` : null,
        authenticatedFetcher,
        { revalidateOnFocus: false, shouldRetryOnError: false }
    );

    return (
        <Paper
            sx={{
                p: 2.5,
                mt: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(KG_COLOR, 0.35)}`,
                background: alpha(KG_COLOR, 0.04),
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                <HubIcon sx={{ color: KG_COLOR, fontSize: 24 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                    Knowledge Graph
                </Typography>
                <Chip
                    label="cross-report intelligence"
                    size="small"
                    sx={{ height: 20, fontSize: '0.65rem', color: KG_COLOR, bgcolor: alpha(KG_COLOR, 0.12), border: `1px solid ${alpha(KG_COLOR, 0.3)}` }}
                />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Separate lookup from the database results above: this checks whether the indicator
                exists as a canonical entity in the STIX Constellation graph, where it is connected
                to malware, actors and CVEs across all reports.
            </Typography>

            {isLoading && <Skeleton variant="rounded" height={56} />}

            {error && (
                <Typography variant="caption" color="text.disabled">
                    Knowledge Graph unavailable right now — database results above are unaffected.
                </Typography>
            )}

            {data && data.results.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Not tracked as a canonical indicator in the Knowledge Graph — it appears only as
                    a raw extraction in the database above (or not at all).
                </Typography>
            )}

            {data && data.results.length > 0 && (
                <Stack spacing={1}>
                    {data.results.map((match) => (
                        <Paper
                            key={match.id}
                            sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                bgcolor: alpha(KG_COLOR, 0.06),
                                border: `1px solid ${alpha(KG_COLOR, 0.2)}`,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: cyber.fontMono, fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                    {match.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Canonical {match.type} · seen in {match.report_count} report{match.report_count === 1 ? '' : 's'}
                                </Typography>
                            </Box>
                            <Button
                                component={RouterLink}
                                to={`/entity/${match.id}`}
                                size="small"
                                variant="outlined"
                                startIcon={<AccountBoxIcon />}
                                sx={{ textTransform: 'none', flexShrink: 0, borderColor: alpha(KG_COLOR, 0.5), color: KG_COLOR }}
                            >
                                View in Knowledge Graph
                            </Button>
                        </Paper>
                    ))}
                </Stack>
            )}
        </Paper>
    );
};

export default KGIndicatorMatches;
